/**
 * Hosts the render backend's canvas and keeps its size in sync with the
 * layout (architecture §10.2). Rendering is on demand, so a resize asks the
 * frame loop for exactly one new frame. The viewport camera learns the size
 * too, for projection and the aspect ratio.
 */
import { useEffect, useRef } from "react";
import { useEditor } from "../EditorContext";

export function ViewportSurface() {
  const { render, camera } = useEditor();
  const { backend, frameLoop } = render;
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    const canvas = backend.canvas;
    if (!host) return;
    if (canvas) {
      canvas.classList.add("kn-viewport__canvas");
      host.appendChild(canvas);
    }

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      camera.setViewportSize(width, height);
      // The backend caps it at what its render quality can afford.
      backend.resize(width, height, window.devicePixelRatio);
      frameLoop.invalidate("viewport resize");
    });
    observer.observe(host);

    return () => {
      observer.disconnect();
      canvas?.remove();
    };
  }, [backend, frameLoop, camera]);

  return (
    <div ref={hostRef} className="kn-viewport__surface" data-testid="viewport">
      {!backend.canvas && (
        <div className="kn-viewport--unavailable" role="status">
          Viewport unavailable: no WebGPU or WebGL2 adapter
        </div>
      )}
    </div>
  );
}
